import React from 'react';
import useUser from '../global/useUser';
import { EmployeeToBusiness, LoadedUser } from '../../interfaces';
import useBusinessOrganization from '../businesses/useBusinessOrganization';

type VirtualDefaultsEmployee = Pick<
  EmployeeToBusiness,
  | 'employeeId'
  | 'consultationVirtualByDefault'
  | 'consultationPermanentVirtualLink'
  | 'defaultVirtualProvider'
>;

export default function useBusinessEmployee(
  employeeId?: string,
  businessId?: string | null,
) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const { businesses } = useBusinessOrganization();

  const employee = React.useMemo<VirtualDefaultsEmployee | undefined>(() => {
    if (!employeeId) return undefined;

    const homeEmployee = loadedUser?.business?.employees?.find(
      e => e.employeeId === employeeId,
    );
    if (homeEmployee) {
      return {
        employeeId: homeEmployee.employeeId,
        consultationVirtualByDefault: homeEmployee.consultationVirtualByDefault,
        consultationPermanentVirtualLink:
          homeEmployee.consultationPermanentVirtualLink,
        defaultVirtualProvider: homeEmployee.defaultVirtualProvider,
      };
    }

    const orgEmployeeMapping = businesses
      .find(business => business.businessId === businessId)
      ?.employeesToBusinesses.find(etb => etb.employeeId === employeeId);
    if (!orgEmployeeMapping) return undefined;

    return {
      employeeId: orgEmployeeMapping.employeeId,
      consultationVirtualByDefault:
        orgEmployeeMapping.employee.consultationVirtualByDefault,
      consultationPermanentVirtualLink:
        orgEmployeeMapping.employee.consultationPermanentVirtualLink,
      defaultVirtualProvider:
        orgEmployeeMapping.employee.defaultVirtualProvider,
    };
  }, [employeeId, loadedUser?.business?.employees, businesses, businessId]);

  const isPhoneProvider = employee?.defaultVirtualProvider === 'PHONE';
  const employeeHasVirtualUrl =
    !!employee?.consultationPermanentVirtualLink || isPhoneProvider;

  return { employee, isPhoneProvider, employeeHasVirtualUrl };
}
